import { type ColumnDef } from "@tanstack/react-table";
import { ScrollText } from "lucide-react";

import { AppShell } from "@/components/AppShell";
import { DataTable } from "@/components/data-table";
import { EmptyState } from "@/components/empty-state";
import { StatusPill } from "@/components/status-pill";
import { useAuditLog } from "@/hooks/api/audit";
import { formatTimestamp } from "@/lib/format";
import type { AuditLogEntry } from "@/types/api";

type StatusKind = "success" | "pending" | "warning" | "danger" | "info" | "deadline";

const ACTION_KIND: Record<string, StatusKind> = {
  LOGIN_SUCCESS: "success",
  LOGIN_FAILED: "danger",
  STAFF_CREATED: "success",
  STAFF_DEACTIVATED: "warning",
  STAFF_REACTIVATED: "success",
  STAFF_PASSWORD_RESET: "info",
  FLOW_PUBLISHED: "success",
  FLOW_UNPUBLISHED: "warning",
};

const ACTION_LABEL: Record<string, string> = {
  LOGIN_SUCCESS: "Login succeeded",
  LOGIN_FAILED: "Login failed",
  STAFF_CREATED: "Staff account created",
  STAFF_DEACTIVATED: "Staff account deactivated",
  STAFF_REACTIVATED: "Staff account reactivated",
  STAFF_PASSWORD_RESET: "Staff password reset",
  FLOW_PUBLISHED: "Script published",
  FLOW_UNPUBLISHED: "Script unpublished",
};

export default function AuditLogPage() {
  const { data, isLoading, error } = useAuditLog();

  const columns: ColumnDef<AuditLogEntry>[] = [
    {
      accessorKey: "created_at",
      header: "When",
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-sm text-slate-600">
          {formatTimestamp(row.original.created_at)}
        </span>
      ),
    },
    {
      accessorKey: "action",
      header: "Action",
      cell: ({ row }) => (
        <StatusPill kind={ACTION_KIND[row.original.action] ?? "info"}>
          {ACTION_LABEL[row.original.action] ?? row.original.action}
        </StatusPill>
      ),
    },
    {
      accessorKey: "actor_email",
      header: "By",
      cell: ({ row }) =>
        row.original.actor_email ? (
          <div>
            <div className="text-sm text-slate-900">
              {row.original.actor_display_name || row.original.actor_email}
            </div>
            <div className="text-xs text-slate-500">{row.original.actor_email}</div>
          </div>
        ) : (
          <span className="text-sm text-slate-400">System</span>
        ),
    },
    {
      accessorKey: "target_table",
      header: "Target",
      cell: ({ row }) =>
        row.original.target_table ? (
          <span className="font-mono text-xs text-slate-500">
            {row.original.target_table}
            {row.original.target_id && `#${row.original.target_id.slice(0, 8)}`}
          </span>
        ) : (
          "—"
        ),
    },
    {
      accessorKey: "ip_address",
      header: "IP",
      cell: ({ row }) => (
        <span className="font-mono text-xs text-slate-500">{row.original.ip_address ?? "—"}</span>
      ),
    },
  ];

  return (
    <AppShell>
      <h1 className="m-0 text-2xl font-bold text-slate-900">Audit log</h1>
      <p className="mt-1 text-sm text-slate-600">
        Who did what, and when — logins, staff account changes, and flow publishing.
      </p>

      <div className="mt-6">
        <DataTable
          columns={columns}
          data={data?.results ?? []}
          loading={isLoading}
          error={error ? "Couldn't load the audit log." : undefined}
          pageSize={50}
          empty={
            <EmptyState
              icon={ScrollText}
              title="Nothing logged yet"
              description="Account changes and script publishing will show up here."
            />
          }
        />
      </div>
    </AppShell>
  );
}
